import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import adminApiService from '../../services/adminApiService';
import toast from 'react-hot-toast';
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  FunnelIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';

interface Report {
  id: string;
  name: string;
  description: string;
  type: 'user_activity' | 'financial' | 'course_performance' | 'job_analytics' | 'verification' | 'system_performance';
  format: 'PDF' | 'CSV' | 'Excel';
  status: 'pending' | 'generating' | 'completed' | 'failed';
  requestedBy: string;
  requestedAt: Date;
  completedAt?: Date;
  downloadUrl?: string;
  parameters: {
    dateRange: { start: Date; end: Date };
    filters?: { [key: string]: any };
  };
  fileSize?: string;
}

interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  type: string;
  category: string;
  parameters: {
    name: string;
    type: 'date_range' | 'dropdown' | 'checkbox' | 'text';
    label: string;
    options?: string[];
    required: boolean;
  }[];
}

const Reports: React.FC = () => {
  const { adminUser, hasPermission } = useAdmin();
  const [reports, setReports] = useState<Report[]>([]);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [reportParameters, setReportParameters] = useState<{ [key: string]: any }>({});

  useEffect(() => {
    fetchReports();
    fetchTemplates();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      
      const response = await adminApiService.getReports();
      if (response.success && response.data) {
        // Transform backend export data to frontend report format
        const backendExports = response.data.exports || [];
        const transformedReports: Report[] = backendExports.map((exportItem: any) => ({
          id: exportItem.id,
          name: `${exportItem.dataType} Export`,
          description: `${exportItem.format.toUpperCase()} export of ${exportItem.dataType} data`,
          type: exportItem.dataType,
          format: exportItem.format.toUpperCase(),
          status: exportItem.status === 'completed' ? 'completed' : 
                 exportItem.status === 'failed' ? 'failed' :
                 exportItem.status === 'processing' ? 'generating' : 'pending',
          requestedBy: exportItem.requestedBy || 'Unknown Admin',
          requestedAt: exportItem.requestedAt ? new Date(exportItem.requestedAt.seconds * 1000) : new Date(),
          completedAt: exportItem.completedAt ? new Date(exportItem.completedAt.seconds * 1000) : undefined,
          downloadUrl: exportItem.downloadUrl || undefined,
          parameters: {
            dateRange: {
              start: exportItem.filters?.startDate ? new Date(exportItem.filters.startDate) : new Date(),
              end: exportItem.filters?.endDate ? new Date(exportItem.filters.endDate) : new Date()
            },
            filters: exportItem.filters || {}
          },
          fileSize: exportItem.fileSize || 'Unknown'
        }));

        setReports(transformedReports);
        
        // Show a message if the feature isn't fully implemented
        if (response.message && transformedReports.length === 0) {
          toast(response.message);
        }
      } else {
        throw new Error('Failed to load reports');
      }
    } catch (error: any) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const mockTemplates: ReportTemplate[] = [
        {
          id: 'template1',
          name: 'User Activity Report',
          description: 'Detailed analysis of user engagement and activity patterns',
          type: 'user_activity',
          category: 'Users',
          parameters: [
            {
              name: 'dateRange',
              type: 'date_range',
              label: 'Date Range',
              required: true
            },
            {
              name: 'userType',
              type: 'dropdown',
              label: 'User Type',
              options: ['all', 'students', 'employers'],
              required: false
            },
            {
              name: 'includeInactive',
              type: 'checkbox',
              label: 'Include Inactive Users',
              required: false
            }
          ]
        },
        {
          id: 'template2',
          name: 'Financial Summary',
          description: 'Revenue, subscriptions, and payment analytics',
          type: 'financial',
          category: 'Finance',
          parameters: [
            {
              name: 'dateRange',
              type: 'date_range',
              label: 'Date Range',
              required: true
            },
            {
              name: 'includeTaxes',
              type: 'checkbox',
              label: 'Include Tax Breakdown',
              required: false
            }
          ]
        },
        {
          id: 'template3',
          name: 'Course Performance Analytics',
          description: 'Course completion rates, scores, and engagement metrics',
          type: 'course_performance',
          category: 'Education',
          parameters: [
            {
              name: 'dateRange',
              type: 'date_range',
              label: 'Date Range',
              required: true
            },
            {
              name: 'category',
              type: 'dropdown',
              label: 'Course Category',
              options: ['all', 'technology', 'business', 'design', 'marketing'],
              required: false
            },
            {
              name: 'minEnrollments',
              type: 'text',
              label: 'Minimum Enrollments',
              required: false
            }
          ]
        },
        {
          id: 'template4',
          name: 'Job Platform Analytics',
          description: 'Job postings, applications, and success metrics',
          type: 'job_analytics',
          category: 'Jobs',
          parameters: [
            {
              name: 'dateRange',
              type: 'date_range',
              label: 'Date Range',
              required: true
            },
            {
              name: 'jobCategory',
              type: 'dropdown',
              label: 'Job Category',
              options: ['all', 'technology', 'healthcare', 'finance', 'education'],
              required: false
            }
          ]
        },
        {
          id: 'template5',
          name: 'Verification Status Report',
          description: 'Identity and business verification processing metrics',
          type: 'verification',
          category: 'Security',
          parameters: [
            {
              name: 'dateRange',
              type: 'date_range',
              label: 'Date Range',
              required: true
            },
            {
              name: 'verificationType',
              type: 'dropdown',
              label: 'Verification Type',
              options: ['all', 'identity', 'business'],
              required: false
            }
          ]
        },
        {
          id: 'template6',
          name: 'System Performance Report',
          description: 'Server performance, uptime, and technical metrics',
          type: 'system_performance',
          category: 'Technical',
          parameters: [
            {
              name: 'dateRange',
              type: 'date_range',
              label: 'Date Range',
              required: true
            },
            {
              name: 'includeErrors',
              type: 'checkbox',
              label: 'Include Error Logs',
              required: false
            }
          ]
        }
      ];

      setTemplates(mockTemplates);
    } catch (error: any) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const handleGenerateReport = async (templateId: string, parameters: { [key: string]: any }) => {
    try {
      const template = templates.find(t => t.id === templateId);
      if (!template) {
        throw new Error('Template not found');
      }

      // Map template type to backend data type
      const dataTypeMap: { [key: string]: string } = {
        'user_activity': 'users',
        'course_performance': 'courses',
        'job_analytics': 'jobs',
        'verification': 'verifications',
        'financial': 'applications' // or could be a separate financial endpoint
      };

      const dataType = dataTypeMap[template.type] || 'users';
      const format = parameters.format || 'csv';

      const response = await adminApiService.generateReport(dataType, format, {
        filters: parameters,
        dateRange: parameters.dateRange
      });

      if (response.success) {
        toast.success('Report generation started');
        setShowCreateModal(false);
        fetchReports(); // Refresh the reports list
      } else {
        throw new Error(response.message || 'Failed to generate report');
      }
    } catch (error: any) {
      console.error('Generate report error:', error);
      toast.error('Failed to generate report: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDownloadReport = async (reportId: string) => {
    try {
      const blob = await adminApiService.downloadReport(reportId);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report-${reportId}.zip`; // or get filename from response headers
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully');
    } catch (error: any) {
      console.error('Download report error:', error);
      toast.error('Failed to download report: ' + (error.message || 'Unknown error'));
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon, label: 'Pending' },
      generating: { color: 'bg-blue-100 text-blue-800', icon: ClockIcon, label: 'Generating' },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, label: 'Completed' },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircleIcon, label: 'Failed' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const getTypeColor = (type: string) => {
    const colors = {
      user_activity: 'bg-blue-100 text-blue-800',
      financial: 'bg-green-100 text-green-800',
      course_performance: 'bg-purple-100 text-purple-800',
      job_analytics: 'bg-orange-100 text-orange-800',
      verification: 'bg-indigo-100 text-indigo-800',
      system_performance: 'bg-gray-100 text-gray-800'
    };
    return colors[type as keyof typeof colors] || colors.user_activity;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
          <p className="mt-2 text-sm text-gray-700">
            Generate and manage comprehensive platform reports
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <DocumentTextIcon className="h-4 w-4 mr-2" />
            Generate New Report
          </button>
        </div>
      </div>

      {/* Report Templates Grid */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Available Report Templates</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {templates.map((template) => (
              <div
                key={template.id}
                className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500 cursor-pointer"
                onClick={() => {
                  setSelectedTemplate(template);
                  setShowCreateModal(true);
                }}
              >
                <div className="flex-shrink-0">
                  <DocumentTextIcon className="h-8 w-8 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="absolute inset-0" aria-hidden="true" />
                  <p className="text-sm font-medium text-gray-900">{template.name}</p>
                  <p className="text-sm text-gray-500 truncate">{template.description}</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-2 ${getTypeColor(template.type)}`}>
                    {template.category}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Reports */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Recent Reports</h3>
          <ul className="divide-y divide-gray-200">
            {reports.map((report) => (
              <li key={report.id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center min-w-0 flex-1">
                      <div className="flex-shrink-0 mr-4">
                        <DocumentTextIcon className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {report.name}
                          </p>
                          {getStatusBadge(report.status)}
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(report.type)}`}>
                            {report.format}
                          </span>
                        </div>
                        
                        <div className="flex items-center text-sm text-gray-500 space-x-4">
                          <div className="flex items-center">
                            <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                            Requested: {new Date(report.requestedAt).toLocaleDateString()}
                          </div>
                          {report.completedAt && (
                            <div className="flex items-center">
                              <CheckCircleIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                              Completed: {new Date(report.completedAt).toLocaleDateString()}
                            </div>
                          )}
                          {report.fileSize && (
                            <div className="flex items-center">
                              <DocumentArrowDownIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                              {report.fileSize}
                            </div>
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-600 mt-2">{report.description}</p>
                        
                        <div className="flex items-center text-xs text-gray-500 mt-2">
                          <span>By: {report.requestedBy}</span>
                          <span className="mx-2">•</span>
                          <span>
                            {new Date(report.parameters.dateRange.start).toLocaleDateString()} - {' '}
                            {new Date(report.parameters.dateRange.end).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="ml-2 flex-shrink-0 flex space-x-2">
                      {report.status === 'completed' && (
                        <button
                          onClick={() => handleDownloadReport(report.id)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                          Download
                        </button>
                      )}
                      <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        <EyeIcon className="h-4 w-4 mr-1" />
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {reports.length === 0 && (
        <div className="text-center py-12">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No reports generated yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by generating your first report using one of the templates above.
          </p>
        </div>
      )}

      {/* Generate Report Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setShowCreateModal(false)}>
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Generate Report: {selectedTemplate?.name}
              </h3>
              
              {selectedTemplate && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 mb-4">{selectedTemplate.description}</p>
                  
                  {selectedTemplate.parameters.map((param) => (
                    <div key={param.name}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {param.label} {param.required && <span className="text-red-500">*</span>}
                      </label>
                      
                      {param.type === 'date_range' && (
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="date"
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            placeholder="Start Date"
                          />
                          <input
                            type="date"
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            placeholder="End Date"
                          />
                        </div>
                      )}
                      
                      {param.type === 'dropdown' && (
                        <select className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                          {param.options?.map((option) => (
                            <option key={option} value={option}>
                              {option.charAt(0).toUpperCase() + option.slice(1)}
                            </option>
                          ))}
                        </select>
                      )}
                      
                      {param.type === 'checkbox' && (
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-600">{param.label}</span>
                        </div>
                      )}
                      
                      {param.type === 'text' && (
                        <input
                          type="text"
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          placeholder={param.label}
                        />
                      )}
                    </div>
                  ))}
                  
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      onClick={() => setShowCreateModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleGenerateReport(selectedTemplate.id, reportParameters)}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Generate Report
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;